import * as Headless from '@headlessui/react';
import { TableCell } from '@oysterjs/ui/table';
import React from 'react';
import { CsvCellEditor } from './cell-editor';
import { Pills, showsAsPills } from './pills';
import { CloseOnScroll, RevealOnOpen } from './scroll';
import { type DiffChange } from '~/components/csv-import/model/diff';
import {
  splitList,
  type CsvColumnWidth,
  type CsvField,
  type CsvFieldType
} from '~/components/csv-import/model/schema';

/** A record's value for one field, falling back to the record it matched. */
const cellValue = (change: DiffChange, key: string): string =>
  change.values?.[key] ?? change.current?.[key] ?? '';

/** Text colours for a cell's value, in the same hues as the pill tones. */
const VALUE_TEXT = {
  invalid: 'text-red-700 dark:text-red-300',
  duplicate: 'text-amber-700 dark:text-amber-300',
  added: 'text-emerald-700 dark:text-emerald-400',
  plain: 'text-neutral-800 dark:text-neutral-100'
};

// Fixed, so editing a cell never resizes its column. Goes on the header and the cells:
// the table sizes a column from all of them. Whole class names, so Tailwind finds them.
const WIDTHS: Record<CsvColumnWidth, string> = {
  xs: 'w-[5rem] min-w-[5rem] max-w-[5rem]',
  sm: 'w-[8rem] min-w-[8rem] max-w-[8rem]',
  md: 'w-[12rem] min-w-[12rem] max-w-[12rem]',
  lg: 'w-[16rem] min-w-[16rem] max-w-[16rem]',
  xl: 'w-[20rem] min-w-[20rem] max-w-[20rem]'
};

const TYPE_WIDTHS: Record<CsvFieldType, string> = {
  string: WIDTHS.md,
  number: 'w-[6rem] min-w-[6rem] max-w-[6rem]',
  boolean: 'w-[6rem] min-w-[6rem] max-w-[6rem]',
  date: WIDTHS.sm,
  enum: WIDTHS.sm,
  // Fits "+442079460958".
  phone: 'w-[10rem] min-w-[10rem] max-w-[10rem]',
  email: WIDTHS.lg
};

// Room for two pills and the count.
const LIST_WIDTH = 'w-[15rem] min-w-[15rem] max-w-[15rem]';

export const columnWidth = (field: CsvField) => {
  if (field.width) {
    return WIDTHS[field.width];
  }
  return field.multiple ? LIST_WIDTH : TYPE_WIDTHS[field.type];
};

/** Memoised with stable props: a grid holds hundreds of cells, each with an editor. */
export const ChangeCell = React.memo(function ChangeCell({
  change,
  field,
  messages,
  duplicateOnly,
  editable,
  editCell,
  leaveCell,
  extensionLabel
}: {
  change: DiffChange;
  field: CsvField;
  messages?: string[];
  /** Whether the messages only say another row names the same record. */
  duplicateOnly?: boolean;
  editable: boolean;
  editCell: (line: number, key: string, value: string) => void;
  /** Told once the cell has been left after an edit. */
  leaveCell: () => void;
  /** For a phone field: the label of the column its extension goes in. */
  extensionLabel?: string;
}) {
  const fieldChange = change.fieldChanges?.find((candidate) => candidate.key === field.key);
  const committed = cellValue(change, field.key);

  // Local until leaving the cell re-checks the file.
  const [draft, setDraft] = React.useState<string>();
  const value = draft ?? committed;

  const edit = (next: string) => {
    setDraft(next);
    if (change.line !== undefined) {
      editCell(change.line, field.key, next);
    }
  };

  // On `change`, not `committed`: an add-only list can re-check to the value it had.
  React.useEffect(() => setDraft(undefined), [change]);

  const cell = React.useRef<HTMLElement | null>(null);
  const panel = React.useRef<HTMLElement | null>(null);

  const pills = showsAsPills(field);
  const messageTone = messages ? (duplicateOnly ? 'duplicate' : 'invalid') : undefined;
  const tone = messageTone ?? (fieldChange ? 'added' : 'plain');
  const oldValue =
    fieldChange &&
    (pills ? (
      <Pills raw={fieldChange.from} tone="removed" />
    ) : (
      <div className="truncate text-red-600 line-through dark:text-red-400">
        {fieldChange.from || '—'}
      </div>
    ));
  const blank = <span className={messages ? '' : 'text-neutral-300 dark:text-neutral-600'}>—</span>;
  // `value` is the change's new value, or the draft while one is being typed.
  const newValue = pills ? (
    <Pills raw={value} tone={tone} />
  ) : (
    <div className={`truncate ${fieldChange ? 'font-medium ' : ''}${VALUE_TEXT[tone]}`}>
      {value || (fieldChange ? '—' : blank)}
    </div>
  );
  const body =
    fieldChange && pills ? (
      // Old and new side by side: pills are short, and the row keeps its height.
      <div className="flex flex-wrap items-center gap-1">
        {oldValue}
        {newValue}
      </div>
    ) : (
      <>
        {oldValue}
        {newValue}
      </>
    );

  return (
    <TableCell
      // Messages ride on the cell, not under it, so every row stays the same height.
      title={messages?.join('\n') || (pills ? splitList(value).join(', ') : value) || undefined}
      className={`scroll-mt-10 align-top ${columnWidth(field)} ${editable ? 'hover:bg-neutral-100/70 dark:hover:bg-neutral-700/50' : ''}`}
    >
      {body}
      {editable && (
        <Headless.Popover>
          <Headless.PopoverButton
            aria-label={value || field.label}
            data-cell-editor
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              cell.current = event.currentTarget.closest('td');
            }}
            // Overlays the cell so the whole cell is the click target.
            className={({ open }: { open: boolean }) =>
              `absolute inset-0 focus:outline-none data-[focus]:ring-2 data-[focus]:ring-inset data-[focus]:ring-sky-500 dark:data-[focus]:ring-sky-400 ${
                open ? 'bg-sky-500/10 ring-1 ring-inset ring-sky-400 dark:ring-sky-500' : ''
              }`
            }
          />
          <Headless.PopoverPanel
            ref={panel}
            // Headless UI anchoring flips and shifts the editor to stay inside the
            // grid, and caps its height so a long list scrolls within itself.
            anchor={{ to: 'bottom start', gap: 4, padding: 8 }}
            className="z-20 w-72 max-h-[var(--anchor-max-height)] overflow-y-auto rounded-lg border border-neutral-200 bg-white p-2 shadow-lg focus:outline-none dark:border-neutral-600 dark:bg-neutral-800"
          >
            {({ close }: { close: () => void }) => (
              <>
                <CloseOnScroll close={close} panel={panel} />
                <RevealOnOpen cell={cell} />
                <CsvCellEditor
                  field={field}
                  value={value}
                  onChange={edit}
                  onClose={close}
                  onLeave={leaveCell}
                  extensionLabel={extensionLabel}
                />
              </>
            )}
          </Headless.PopoverPanel>
        </Headless.Popover>
      )}
    </TableCell>
  );
});
